import type { AfterViewInit, OnInit } from '@angular/core';
import type { TrackingErrorEventDetail } from '@wizbii/utils/tracking';
import type { Observable } from 'rxjs';

import { isPlatformServer } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  HostBinding,
  HostListener,
  inject,
  NgZone,
  PLATFORM_ID,
  Renderer2,
  signal,
} from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import Bugsnag from '@bugsnag/js';
import { trackingConfig } from '@config/tracking.config';
import { BreakpointsService } from '@core/services/breakpoints.service';
import { MetaCustomService } from '@core/services/meta-custom.service';
import { metadata } from '@core/utils';
import { environment } from '@environment';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';
import { HelpFormRoutingEnum } from '@features/help-form/help-form-routing.enum';
import { Store } from '@ngrx/store';
import { trackers } from '@stores/services/services.selector';
import { identityCard, isInitialized, user } from '@stores/session/session-ngrx.selector';
import { jwtTokens, NGRX_JWT_STATE_CONFIG } from '@wizbii-utils/angular/stores';
import { createTrackingConfig, setupTracking, track, trackingErrorEventName } from '@wizbii/utils/tracking';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, combineLatest, from, fromEvent, of } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';

@Component({
  selector: 'app-core',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  imports: [RouterOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutComponent implements OnInit, AfterViewInit {
  /**
   * Use class `hover-on` in CSS as follows:
   * `:host-context(.hover-on) .link:hover { ... }`
   */
  @HostBinding('class.hover-on') hoverEnabled = true;

  @HostBinding('class.hover-off')
  get hoverDisabled(): boolean {
    return !this.hoverEnabled;
  }

  /**
   * Class `focus-off` disables all outlines automatically.
   */
  @HostBinding('class.focus-off') focusOutlineDisabled = false;

  get applicationId(): string {
    return environment.applicationId;
  }

  trackingIsSetup$ = new BehaviorSubject<boolean>(false);
  lang = environment.i18n.lang;

  readonly #cookieService = inject(CookieService);
  readonly #jwtStateConfig = inject(NGRX_JWT_STATE_CONFIG);
  readonly #store = inject(Store);
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  readonly #breakpointsService = inject(BreakpointsService);
  readonly #renderer = inject(Renderer2);
  readonly #metaCustomService = inject(MetaCustomService);
  readonly #platformId = inject(PLATFORM_ID);
  readonly #ngZone = inject(NgZone);
  readonly #matIconRegistry = inject(MatIconRegistry);
  readonly #domSanitizer = inject(DomSanitizer);

  readonly #previousUrl = signal<URL | undefined>(undefined);

  constructor() {
    effect(() => {
      if (this.isInitializedSig()) {
        this.initTracking();
      }
    });

    this.#ngZone.runOutsideAngular(() => {
      const domain = environment.deployAssetsUrl ?? '/assets';
      this.#matIconRegistry.addSvgIconSet(this.#domSanitizer.bypassSecurityTrustResourceUrl(`${domain}/sprite.svg`));
    });
  }

  ngOnInit(): void {
    this.reloadPageAfterBFCacheNavigationUnlogged();

    this.#setCanonical();

    if (document.documentElement.lang === '') {
      document.documentElement.lang = this.lang;
    }

    if (isPlatformServer(this.#platformId)) {
      return;
    }

    /**
     * Disable hover on `touchstart` to cover browsers that do not support pointer events.
     * https://caniuse.com/#feat=pointer
     */
    fromEvent(window, 'touchstart', { passive: true }).subscribe(() => {
      this.hoverEnabled = false;
    });

    this.#route.queryParamMap
      .pipe(
        map((queryParamMap) => queryParamMap.get('awc')),
        filter((awc) => !!awc),
        take(1)
      )
      .subscribe({
        next: (awc) => {
          const cookieSubDomain = ['', ...document.location.hostname.split('.').slice(-2)].join('.');
          const cookieDomain = cookieSubDomain === '.localhost' ? 'localhost' : cookieSubDomain;
          const msIn390Days = 1000 * 3600 * 24 * 390;
          const expiry = new Date(Date.now() + msIn390Days);

          if (awc) {
            this.#cookieService.set(
              'awc',
              awc,
              expiry,
              '/',
              cookieDomain,
              cookieDomain !== 'localhost',
              cookieDomain === 'localhost' ? 'Lax' : 'None'
            );
          }
        },
      });
  }

  ngAfterViewInit(): void {
    if (isPlatformServer(this.#platformId)) {
      return;
    }

    combineLatest([this.#router.events, this.trackingIsSetup$])
      .pipe(
        filter(([event]) => event instanceof NavigationEnd),
        map(([event, _]) => [(event as NavigationEnd).urlAfterRedirects, _]),
        map(([_, trackingIsSetup]) => {
          const url = this.#cleanUrlForTracking(window.location.href);
          const referrer = this.#cleanUrlForTracking(this.#previousUrl()?.href ?? document.referrer);

          return [
            {
              url,
              referrer,
            },
            trackingIsSetup,
          ];
        })
      )
      .subscribe({
        next: ([event, trackingIsSetup]) => {
          window.addEventListener(trackingErrorEventName, (errorEvent) => {
            this.handleTrackingShadowError(errorEvent['detail']);
          });

          this.#pageView(
            trackingIsSetup as boolean,
            event as {
              url: URL;
              referrer: URL;
            }
          );
        },
      });
  }

  handleTrackingShadowError(details?: TrackingErrorEventDetail): void {
    if (!details) {
      return;
    }

    Bugsnag.notify(new Error('Tracking shadow error'), (e) => {
      e.severity = details.level;

      e.addMetadata('details', {
        cause: details.cause,
        message: details.message,
        level: details.level,
      });
    });
  }

  readonly #cleanUrlForTracking = (url: string): URL => {
    const cleanedUrl = new URL(url, window.location.origin);
    for (const queryParam of [
      'token',
      'login-token',
      'activation-token',
      'capsule-token',
      'jwt-token',
      'jwt-refresh-token',
    ]) {
      cleanedUrl.searchParams.delete(queryParam);
    }
    return cleanedUrl;
  };

  private reloadPageAfterBFCacheNavigationUnlogged() {
    window.addEventListener('pageshow', (event) => {
      if (event.persisted && !this.#cookieService.get(this.#jwtStateConfig.jwtCookieName)) {
        // Force a reload if the user has logged out and come back from bf cache.
        window.location.reload();
      }
    });
  }

  isInitializedSig = this.#store.selectSignal(isInitialized);
  userSig = this.#store.selectSignal(user);
  identityCardSig = this.#store.selectSignal(identityCard);
  private initTracking(): void {
    let initTracking$: Observable<boolean | undefined> = of(undefined);

    if (this.isInitializedSig()) {
      initTracking$ = combineLatest([this.#store.select(jwtTokens), this.#store.select(trackers)]).pipe(
        filter(([_tokens, trackers]) => !!trackers),
        take(1),
        map(([tokens, trackers]) => {
          environment.awinMerchantId = trackers?.awinMerchantId ?? '';

          return [tokens, trackingConfig(trackers, this.userSig(), this.identityCardSig().id)];
        }),
        map(([tokens, updatedTrackingConfig]) =>
          createTrackingConfig(updatedTrackingConfig as any, {
            ...((tokens as any)?.token ? { token: (tokens as any).token } : {}),
            ...(this.identityCardSig() ? { firstName: (this.identityCardSig() as any).firstName } : {}),
            ...(this.identityCardSig() ? { lastName: (this.identityCardSig() as any).lastName } : {}),
          })
        ),
        switchMap((config) => from(setupTracking(config))),
        switchMap(() => this.trackingIsSetup$),
        tap((trackingIsSetup) => {
          this.#pageView(trackingIsSetup, undefined, true);
        })
      );
    }

    combineLatest([initTracking$, this.#breakpointsService.isMobile$]).subscribe(() => {
      const bannerElement = document.querySelector('[class^="gdpr_banner"]');
      if (!bannerElement) {
        return;
      }
      if (this.#router.url === `/${FeaturesRoutingEnum.HelpForm}/${HelpFormRoutingEnum.Player}`) {
        this.#renderer.addClass(bannerElement, 'gdpr_banner--top');
        return;
      }
      this.#renderer.removeClass(bannerElement, 'gdpr_banner--top');
    });
  }

  #pageView(
    trackingIsSetup: boolean,
    event?: {
      url: URL;
      referrer: URL;
    },
    first = false
  ): void {
    if (!trackingIsSetup && first) {
      if (event) {
        this.#trackPageViewWithEvent(event);
      } else {
        this.#trackPageViewWithoutEvent();
      }

      this.trackingIsSetup$.next(true);
    } else if (trackingIsSetup) {
      if (event) {
        this.#trackPageViewWithEvent(event);
      } else {
        this.#trackPageViewWithoutEvent();
      }
    }

    this.#setCanonical();
  }

  readonly #trackPageViewWithEvent = (event: { url: URL; referrer: URL }) => {
    track('pageview', {
      url: event.url.href,
      page: event.url.href.replace(event.url.origin, ''),
      referrer: event.referrer.href,
    });
    this.#previousUrl.set(event.url);
  };

  readonly #trackPageViewWithoutEvent = () => {
    const url = this.#cleanUrlForTracking(window.location.href);
    const referrer = this.#cleanUrlForTracking(this.#previousUrl()?.href ?? document.referrer);
    track('pageview', {
      url: url.href,
      page: url.href.replace(url.origin, ''),
      referrer: referrer.href,
    });
    this.#previousUrl.set(url);
  };

  #setCanonical(): void {
    const ogUrl = `${window.location.origin}${window.location.pathname}`;
    this.#metaCustomService.updateOrAddTag({
      property: 'og:url',
      content: ogUrl,
    });
    const canonicalUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
    this.#metaCustomService.updateOrAddCanonicalURL(canonicalUrl);
    this.#metaCustomService.setMetaData(metadata.home, this.lang);
  }

  /**
   * Enable hover if "mouse" pointer event is detected; disable it otherwise.
   * https://developer.mozilla.org/en-US/docs/Web/Events/pointerenter
   */
  @HostListener('pointerenter', ['$event'])
  onPointerEnter(event: any): void {
    const evt: PointerEvent = event; // https://github.com/kulshekhar/ts-jest/issues/1035
    this.hoverEnabled = evt.pointerType === 'mouse';
  }

  @HostListener('mousedown')
  onMouseDown(): void {
    this.focusOutlineDisabled = true;
  }

  @HostListener('keydown.Tab')
  onTabKeyDown(): void {
    this.focusOutlineDisabled = false;
  }
}
